import { formatCurrency } from '@airship-platform/airship-patterns';
import {
  OrganizationSOW,
  PriceSectionEditable,
  ProductCard,
} from '@airship-platform/airship-proposal';
import {
  FlexBox,
  SanitizedTrimmedHtml,
} from '@airship-platform/jam-web/components';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { ProposalTemplateOption } from '../../../data-access/proposalTemplate';
import SOWModal from '../../sow-query/SOWModal';
import { useApplyProposalTemplate } from '../hooks/useApplyProposalTemplate';
import ProposalTemplateDetailCard from './ProposalTemplateDetailCard';
import ProposalTemplateDiscountsList from './ProposalTemplateDiscountsList';

interface ProposalTemplateDetailProps {
  proposalTemplateOption: ProposalTemplateOption;
}

const ProposalTemplateDetail: React.FC<ProposalTemplateDetailProps> = ({
  proposalTemplateOption,
}) => {
  const { lookupMaps } = useApplyProposalTemplate();
  const [selectedSow, setSelectedSow] = useState<OrganizationSOW>();

  const option = useMemo(
    () => ({
      ...proposalTemplateOption,
      totalItems:
        proposalTemplateOption.products.length +
        proposalTemplateOption.modifiers.length,
      totalPrice: proposalTemplateOption.products
        .flatMap((p) =>
          p.prices
            .filter((d) => d.priceType === p.defaultPriceType)
            .map((d) => ({ ...d, quantity: p.quantity }))
        )
        .reduce(
          (total, product) => total + product.value * product.quantity,
          0
        ),
      systemProducts: proposalTemplateOption.products.filter(
        (p) => p.groupingName === 'SYSTEM'
      ),
      addOnProducts: proposalTemplateOption.products.filter(
        (p) => p.groupingName === 'ADDONS'
      ),
      discounts: proposalTemplateOption.modifiers
        .filter((modifier) => modifier.modifierType === 'DISCOUNT')
        .flatMap((d) => {
          const discountAndFee = lookupMaps.modifiers.get(d.discountsAndFeesId);
          return discountAndFee ? [{ ...discountAndFee, ...d }] : [];
        }),
      rebates: proposalTemplateOption.modifiers
        .filter((modifier) => modifier.modifierType === 'REBATE')
        .flatMap((d) => {
          const discountAndFee = lookupMaps.modifiers.get(d.discountsAndFeesId);
          return discountAndFee ? [{ ...discountAndFee, ...d }] : [];
        }),
      sows: proposalTemplateOption.sows.flatMap((d) => {
        const sow = lookupMaps.sows.get(d.sowId);
        return sow ? [{ ...sow, ...d }] : [];
      }),
    }),
    [lookupMaps.modifiers, lookupMaps.sows, proposalTemplateOption]
  );

  return (
    <FlexBox flexDirection={'column'} gap={4} width={'100%'}>
      <FlexBox flexDirection={'column'} gap={1}>
        <Typography variant="h3">{option.name}</Typography>
        <Typography variant="subtitle1">
          {[
            formatCurrency(option.totalPrice),
            `(${option.totalItems} items)`,
          ].join(' ')}
        </Typography>
      </FlexBox>

      <ProposalTemplateDiscountsList discounts={option.discounts} />

      <ProposalTemplateDetailCard
        title="Services and equipment"
        isVisible={!!option.systemProducts.length}
      >
        {option.systemProducts.map((product) => (
          <ProductCard
            key={product.proposalTemplateOptionProductId}
            product={product}
            priceSection={
              <PriceSectionEditable
                prices={product.prices}
                defaultPriceType={product.defaultPriceType}
              />
            }
          />
        ))}
      </ProposalTemplateDetailCard>

      <ProposalTemplateDetailCard
        title="Materials"
        isVisible={!!option.addOnProducts.length}
      >
        {option.addOnProducts.map((product) => (
          <ProductCard
            key={product.proposalTemplateOptionProductId}
            product={product}
            priceSection={
              <PriceSectionEditable
                prices={product.prices}
                defaultPriceType={product.defaultPriceType}
              />
            }
          />
        ))}
      </ProposalTemplateDetailCard>

      <ProposalTemplateDetailCard
        title="Rebates"
        isVisible={!!option.rebates.length}
      >
        {option.rebates.map((rebate) => (
          <ProductCard
            key={rebate.proposalTemplateOptionModifierId}
            product={{
              description: rebate.description || '',
              name: rebate.displayName,
              images: rebate.images,
            }}
            priceSection={
              <PriceSectionEditable
                defaultPriceType={'STANDARD'}
                prices={[
                  {
                    value: rebate.amount,
                    currency: 'USD',
                    priceType: 'STANDARD',
                    taxable: rebate.taxable,
                  },
                ]}
                startAdornment="-"
              />
            }
          />
        ))}
      </ProposalTemplateDetailCard>

      <ProposalTemplateDetailCard
        title="Statement of Work"
        isVisible={!!option.sows.length}
      >
        {option.sows.map((sow) => (
          <FlexBox key={sow.sowId} flexDirection={'column'} width={'100%'}>
            <Typography variant="h2">{sow.title}</Typography>
            <Typography variant="body1">{sow.shortDescription}</Typography>
            <SanitizedTrimmedHtml
              html={sow.summary}
              maxLength={10}
              onSeeMore={() => setSelectedSow(sow)}
            />
          </FlexBox>
        ))}
      </ProposalTemplateDetailCard>

      {selectedSow && (
        <SOWModal
          title={selectedSow.title}
          summary={selectedSow.summary}
          open={true}
          onClose={() => setSelectedSow(undefined)}
        />
      )}
    </FlexBox>
  );
};

export default ProposalTemplateDetail;
